import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import useImagesSrc from '../../helper/useImagesSrc';
import { usePreloadImages } from '../../helper/usePreloadImages';
import { selectEventsData } from '../events/eventsSlice';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import MainHeadline from '../../UI/MainHeadline';
import UIButton from '../../UI/UIButton';

const ActiveIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M16.2954 9.60549L11.7274 15.6055C11.5394 15.8525 11.2484 15.9985 10.9384 16.0005H10.9314C10.6244 16.0005 10.3344 15.8585 10.1444 15.6165L7.71237 12.5095C7.37237 12.0755 7.44837 11.4465 7.88337 11.1065C8.31737 10.7655 8.94737 10.8415 9.28737 11.2775L10.9204 13.3635L14.7044 8.39449C15.0384 7.95549 15.6654 7.86949 16.1064 8.20449C16.5454 8.53949 16.6304 9.16649 16.2954 9.60549ZM12.0004 2.00049C6.47737 2.00049 2.00037 6.47749 2.00037 12.0005C2.00037 17.5225 6.47737 22.0005 12.0004 22.0005C17.5234 22.0005 22.0004 17.5225 22.0004 12.0005C22.0004 6.47749 17.5234 2.00049 12.0004 2.00049Z"
			fill="#F58A3C"
		/>
	</svg>
);

export const InactiveIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M16.2954 9.60549L11.7274 15.6055C11.5394 15.8525 11.2484 15.9985 10.9384 16.0005H10.9314C10.6244 16.0005 10.3344 15.8585 10.1444 15.6165L7.7124 12.5095C7.3724 12.0755 7.4484 11.4465 7.8834 11.1065C8.3174 10.7655 8.9474 10.8415 9.2874 11.2775L10.9204 13.3635L14.7044 8.39449C15.0384 7.95549 15.6654 7.86949 16.1064 8.20449C16.5454 8.53949 16.6304 9.16649 16.2954 9.60549ZM12.0004 2.00049C6.4774 2.00049 2.0004 6.47749 2.0004 12.0005C2.0004 17.5225 6.4774 22.0005 12.0004 22.0005C17.5234 22.0005 22.0004 17.5225 22.0004 12.0005C22.0004 6.47749 17.5234 2.00049 12.0004 2.00049Z"
			fill="#EED6C5"
		/>
	</svg>
);

const Wrapper = styled.div`
	box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
	transition: border-radius 0.3s ease;
	width: 100%;
	display: flex;
	flex-direction: column;
	min-width: 343px;
	overflow-y: scroll;
	position: relative;
	margin-bottom: ${({ isMultiple }) => isMultiple && '20px'};
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: none;
	${({ isSpecialFlow }) =>
		isSpecialFlow &&
		css`
			margin-top: 20px;
		`};
	@media screen and (min-width: 450px) {
		margin-top: 20px;
	}
  @media screen and (max-width: 450px) {
    margin-bottom: ${({ isMultiple }) => isMultiple &&  '70px'};
  }
`;

const ContentWrapper = styled.div`
	border-radius: 24px;
	box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
	padding: 16px 8px 8px 8px;
`;

const StyledList = styled.ul`
	list-style: none;
	width: 100%;
`;

const StyledItem = styled.li`
	width: 100%;
	height: 96px;
	display: flex;
	align-items: center;
	border: ${({ active }) =>
		active ? `1px solid #F58A3C` : `1px solid #ffffff`};
	background-color: ${({ active }) => (active ? '#FFE4D0' : '#FEF7F1')};
	padding: 16px;
	border-radius: 16px;
	position: relative;
	cursor: pointer;
	> img {
		height: 64px;
		width: 63px;
		margin-right: 16px;
	}
	> svg {
		position: absolute;
		right: 18px;
	}
	&:not(:last-child) {
		margin-bottom: 8px;
	}
`;

const StyledItemTitle = styled.div`
	font-weight: ${({ active }) => (active ? 600 : 500)};
	font-size: 18px;
	color: #323232;
	text-align: left;
	max-width: ${({isMultiple}) => isMultiple ? '175px' : '214px'};
`;

const TrackContainer = ({
	nextPage,
	pageEvent,
	title,
	options,
	multiple = false,
	onContinueClick,
	onSingleClick,
}) => {
	const eventsData = useSelector(selectEventsData);
	const [initEventSent, setInitEventStatus] = useState(false);
	const [chosenOptions, setChosenOptions] = useState([]);
	const { imagesSources } = useImagesSrc();
	usePreloadImages(
		imagesSources[
			`${nextPage === 'explore-solutions' ? 'explore-solutions' : nextPage}`
		]
	);
	useEffect(() => {
		if (initEventSent) return;
		sendAnalyticsEvents(EVENTS[pageEvent], eventsData);
		setInitEventStatus(true);
	}, [eventsData, initEventSent, pageEvent]);

	const renderOption = (item, i) => {
		const isActive = chosenOptions.includes(item.value);
		const handleClick = () => {
			if (isActive) {
				setChosenOptions(chosenOptions.filter((value) => value !== item.value));
			} else {
				if (multiple) {
					setChosenOptions([...chosenOptions, item.value]);
				} else {
					setChosenOptions(item.value);
					onSingleClick(item.value);
				}
			}
		};
		return (
			<StyledItem
				active={isActive}
				key={item.id}
				onClick={handleClick}
				id={`option-${i + 1}`}
			>
				<img src={item.imgPath} alt={item.name} />
				<StyledItemTitle active={isActive} isMultiple={multiple}>{item.name}</StyledItemTitle>
				{multiple && (isActive ? ActiveIcon : InactiveIcon)}
			</StyledItem>
		);
	};

	return (
		<>
			<Wrapper isMultiple={multiple} id="scrollable-div">
				<ContentWrapper>
					<MainHeadline customStyles={'text-align: left; margin-left: 8px; font-weight: 800; line-height: 36px; font-size: 28px; color: #121212'}>
						{title}
					</MainHeadline>
					<StyledList>{options.map(renderOption)}</StyledList>
				</ContentWrapper>
			</Wrapper>
			{multiple && (
				<UIButton
					onClick={() => onContinueClick(chosenOptions)}
					bottomPosition={'16px'}
					disabled={!chosenOptions.length}
				/>
			)}
		</>
	);
};

export default TrackContainer;
